<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <page-header v-if="props.phoneNumberId"
            class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a :href="`/settings/teams/${teamId}#/phone-numbers`"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Phone Numbers
                </a>
            </template>

            {{ formatPhoneNumber(data.phone_number) }}
        </page-header>
        <step-form-heading v-else
            :icon="faPhone">
            New Phone Number

            <template v-slot:description>
                Find a new phone number to use with Voxie.
            </template>
        </step-form-heading>

        <div :class="{
            'grid grid-cols-12': props.phoneNumberId
        }">
            <page-sidebar v-if="props.phoneNumberId"
                appearance="nested"
                :links="[
                    { href: `/settings/teams/${teamId}/phone-numbers/${props.phoneNumberId}`, label: 'Details' },
                ]"></page-sidebar>
            <form-wrapper
                :sections="[
                    { id: 'search', label: 'Search' },
                    { id: 'sending', label: 'Sending' },
                    FEATURES.contact_groups ? { id: 'group', label: 'Group' } : undefined,
                    { id: 'voice', label: 'Voice' },
                ].filter(section => Boolean(section))"
                :hideNav="Boolean(success || props.phoneNumberId)"
            >

                <vx-alert v-if="success"
                    color="success"
                    :closeable="false"
                    shadow>
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </template>
                    <div class="flex items-center justify-between">
                        <span><strong>Phone Number</strong> {{ props.phoneNumberId ? 'updated' : 'created' }} successfully</span>
                        <vx-button :href="`/settings/teams/${teamId}#/phone-numbers`"
                            size="sm"
                            color="success">Back to
                            Phone Numbers <font-awesome-icon widthAuto
                                :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                    </div>
                </vx-alert>


                <team-phone-number-form-search v-if="!props.phoneNumberId" id="search"
                    v-model="selectedNumber"></team-phone-number-form-search>

                <team-phone-number-form-sending id="sending"
                    v-model="data.use_for_sending"></team-phone-number-form-sending>

                <team-phone-number-form-group v-if="FEATURES.contact_groups" id="group"
                    v-model="data.group_id"></team-phone-number-form-group>

                <team-phone-number-form-voice id="voice"
                    v-model="data"></team-phone-number-form-voice>

                <vx-sticky-footer>
                    <vx-button :href="`/settings/teams/${teamId}#/phone-numbers`"
                        class="w-20"
                        size="lg"
                        color="muted">
                        Cancel
                    </vx-button>
                    <vx-button @click="submit()"
                        :loading="submitting"
                        data-test="submit-button"
                        class="grow"
                        size="lg"
                        color="primary">
                        Create
                    </vx-button>
                </vx-sticky-footer>

            </form-wrapper>
        </div>
    </form-container>
</template>

<script setup>

import {
    VxAlert,
    VxButton,
    VxStickyFooter,
} from '@voxie/frontend-components';
import { inject, onMounted, ref , watch } from 'vue';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { faArrowLeft, faCircleCheck, faPhone, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TeamPhoneNumberFormSearch from './TeamPhoneNumberFormSearch.vue';
import TeamPhoneNumberFormSending from './TeamPhoneNumberFormSending.vue';
import TeamPhoneNumberFormGroup from './TeamPhoneNumberFormGroup.vue';
import TeamPhoneNumberFormVoice from './TeamPhoneNumberFormVoice.vue';
import { adminPhoneNumbersClient , phoneNumbersClient } from '~/services';
import useVuelidate from '@vuelidate/core';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { formatPhoneNumber } from '~/components/filters';
import FormContainer from '~/components/general/FormContainer.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { validateRules } from '~/utils/validation';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';

const FEATURES = inject('FEATURES');

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    phoneNumberId: {
        type: Number,
        required: false,
    }
})

const selectedNumber = ref();

const defaultData = () => ({
    phone_number: '',
    use_for_sending: false,
    group_id: FEATURES.contact_groups ? null : undefined,
    inbound_call_handler: 'core',
    voice_message_text: '',
    forward_voice_to: '',
    forward_voice_send_digits: '',
});

const data = ref(defaultData());

watch(selectedNumber, () => {
    data.value.phone_number = selectedNumber.value?.phone_number;
}, {
    deep: true,
})

const success = ref(false);
const loading = ref(false);
const submitting = ref(false);
const { catchError } = useFormRequestHandler();
const validate = useVuelidate();

onMounted(async () => {
    if (props.phoneNumberId) {
        try {
            loading.value = true;
            const response = await phoneNumbersClient.showPhoneNumber(teamId, props.phoneNumberId);
            data.value = response.data;
        } finally {
            loading.value = false;
        }
    }
})

const submit = async () => {
    success.value = false;

    // when creating enforce phone number selection
    if (!props.phoneNumberId && (!selectedNumber.value?.phone_number || !selectedNumber.value?.iso_country)) {
        toasted.error("Search a phone number and select it before saving", {
            sticky: true,
        });
        return;
    }

    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        let phoneNumberId = props.phoneNumberId;

        if (!phoneNumberId) {
            // purchase number when not created yet
            const purchaseResponse = await adminPhoneNumbersClient.create(
                teamId,
                { vendor: selectedNumber.value.vendor, phone_number: selectedNumber.value.phone_number, country: selectedNumber.value.iso_country }
            );

            phoneNumberId = purchaseResponse.data.id;
        }

        await adminPhoneNumbersClient.update(teamId, phoneNumberId, data.value);

        success.value = true;

        validate.value.$reset();

        if (!props.phoneNumberId) {
            selectedNumber.value = {};
            data.value = defaultData();
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
       catchError(e);
    } finally {
        submitting.value = false;
    }
}
</script>
